import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useApi from '../api';
import CreateAgent from './CreateAgent';
import { AimdocComponentContext } from '@aimdoc/sdk-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { PlusCircle, Users, MessageCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AgentSelectionDrawer } from './AgentSelectionDrawer';
import { pluralize } from './deleteAgent';

type Agent = {
  id: string;
  parent_agent_id: string;
  name: string;
  type: string;
  persona: string;
  goal: string;
  presence_connection_count?: number;
  active_conversation_count?: number;
};

const getAgentInitials = (name?: string) => {
  const words = name?.trim().split(/\s+/).filter(Boolean) ?? [];

  if (!words.length) return 'A';

  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

const AgentLists: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const navigate = useNavigate();
  const { get } = useApi();

  const search = new URLSearchParams(window.location.search);
  const newAgent = search.get('new');

  const toggleModal = () => {
    setShowCreateAgent(false);
    setShowSelectionModal(false);
    navigate('/agents');
  };

  useEffect(() => {
    getAgents();
  }, []);

  const getAgents = async () => {
    try {
      setLoading(true);
      const data = await get('/agents/');
      setAgents(data.data);
      setFilteredAgents(data.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
    setLoading(false);
  };

  const handleSearch = async (search: string) => {
    setSearchInput(search);
    try {
      setLoading(true);
      const data = await get(`/agents/?search=${search}`);
      setAgents(data.data);
      setFilteredAgents(data.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (newAgent === 'true') {
      setShowSelectionModal(true);
    }
  }, [newAgent]);

  const AgentsLoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse rounded-lg border border-border bg-card p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-muted" />
            <div className="space-y-2 flex-1">
              <div className="h-4 bg-muted rounded w-1/2" />
              <div className="h-3 bg-muted rounded w-1/3" />
            </div>
          </div>
          <div className="h-3 bg-muted rounded w-full" />
          <div className="h-3 bg-muted rounded w-3/4" />
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow">
        <h1 className='text-4xl font-bold flex items-center gap-2'>Agents</h1>
        <p className='mt-4 text-sm text-slate-500'>Create and manage your agents here. Select an agent to customize and copy configuration details.</p>
        <AimdocComponentContext
          componentDescription="You are currently in the Agents list page. Here you can see all of the users agents. You can create a new agent, modify an existing agent, or delete an agent."
          componentName="Agents List"
        />
        <Separator className="my-4" />
        <div className='mt-4 mx-auto rounded-md flex flex-row'>
          <div className="relative flex w-full">
            <Input
              type="text"
              id="search"
              placeholder="Search agents..."
              autoComplete="off"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  handleSearch(e.currentTarget.value);
                }
              }}
            />
          </div>
          <div className="relative w-full rounded-lg overflow-hidden">
            <Button onClick={() => setShowSelectionModal(true)} className="absolute right-0 top-0 h-full px-4 rounded-lg focus-within:shadow-lg">
              Create New Agent <PlusCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="mt-4 pb-12">
          {loading ? (
            <AgentsLoadingSkeleton />
          ) : filteredAgents.length ? (
            <TooltipProvider delayDuration={150}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAgents.map((agent) => {
                const connectedCount = Number(agent.presence_connection_count || 0);
                const activeConversationCount = Number(agent.active_conversation_count || 0);

                return (
                  <div
                    key={agent.parent_agent_id}
                    className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-foreground/15 hover:shadow-lg"
                    onClick={() => navigate(`/agents/${agent.parent_agent_id}`)}
                  >
                    <div className="flex items-start justify-between gap-3 p-5 pb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          aria-hidden="true"
                          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40 text-xs font-semibold tracking-wide text-foreground/70 transition-colors group-hover:bg-muted/70 group-hover:text-foreground"
                        >
                          {getAgentInitials(agent.name)}
                        </div>
                        <div className="min-w-0">
                          <Link
                            to={`/agents/${agent.parent_agent_id}`}
                            className="font-semibold text-foreground hover:underline truncate block"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {agent.name || 'Unnamed Agent'}
                          </Link>
                          <AimdocComponentContext
                            componentDescription="You need to click this to go to this agent's details page. Here is where you modify anything for this agent."
                            componentName="Agent Details"
                          />
                          <p className="text-xs text-muted-foreground font-mono truncate">{agent.parent_agent_id}</p>
                        </div>
                      </div>
                      {/* Stat badges: icon + count only, each hidden when zero. The row keeps a
                          fixed height so a card's size is identical with or without badges. */}
                      <div className="flex-shrink-0 flex items-center gap-1.5 min-h-[1.5rem]">
                        {connectedCount > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                                <Users className="h-3 w-3" />
                                {connectedCount}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{pluralize(connectedCount, 'connected visitor')}</TooltipContent>
                          </Tooltip>
                        )}
                        {activeConversationCount > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-1.5 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-400">
                                <MessageCircle className="h-3 w-3" />
                                {activeConversationCount}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{pluralize(activeConversationCount, 'active conversation')}</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>

                    {(agent.persona || agent.goal) && (
                      <div className="grid flex-1 gap-3 border-t border-border/70 bg-muted/[0.12] px-5 py-4">
                        {agent.persona && (
                          <div>
                            <p className="mb-1 text-xs font-medium text-muted-foreground">Persona</p>
                            <p className="line-clamp-2 text-sm leading-5 text-foreground/80">{agent.persona}</p>
                          </div>
                        )}
                        {agent.goal && (
                          <div>
                            <p className="mb-1 text-xs font-medium text-muted-foreground">Goal</p>
                            <p className="line-clamp-2 text-sm leading-5 text-foreground/80">{agent.goal}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </TooltipProvider>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              No agents
            </div>
          )}
        </div>
      </div>
      <AgentSelectionDrawer 
        open={showSelectionModal}
        onOpenChange={(open) => !open && toggleModal()}
        onSelectManual={() => {
          setShowSelectionModal(false);
          setShowCreateAgent(true);
        }}
      />
      {showCreateAgent && (
        <CreateAgent
          showModal={showCreateAgent}
          setShowModal={setShowCreateAgent}
          setAgents={setAgents}
        />
      )}
    </>
  );
};

export default AgentLists;
